import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { Lock, LogIn } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiError } from "@/context/AuthContext";

const Login = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    return <Navigate to={user.role === "referral_partner" ? "/referrals/new" : "/dashboard"} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      navigate(u.role === "referral_partner" ? "/referrals/new" : "/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-gradient-to-br from-slate-700 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-lg">SV</span>
          </div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
            Soar Your Visibility
          </h1>
          <p className="text-slate-400 text-sm mt-1">Secure Portal Login</p>
        </div>

        <Card className="border-slate-700">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" className="mt-1" data-testid="login-email"
                  value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@organization.org" required />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" className="mt-1" data-testid="login-password"
                  value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
              </div>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3" data-testid="login-error">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 text-white gap-2" data-testid="login-submit">
                {loading ? "Signing in..." : <><LogIn className="w-4 h-4" /> Sign In</>}
              </Button>
            </form>
            <p className="text-xs text-slate-500 mt-6 flex items-center gap-1.5 justify-center">
              <Lock className="w-3 h-3" /> Access is restricted to authorized personnel.
            </p>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Link to="/" className="text-slate-400 text-sm hover:text-white transition-colors">← Back to home</Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
